import { Add, Copy, ImportCurve, SearchZoomIn, SearchZoomOut1 } from 'iconsax-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate } from 'react-router';
import { useTypedParams } from 'react-router-typesafe-routes';
import ShareArrow from '@/assets/images/icons/share-arrow.svg?react';
import ZoomFit from '@/assets/images/icons/zoom-fit.svg?react';
import DateDisplay from '@/components/common/date-display';
import type { ImageZoomActions } from '@/components/common/media-content/image-zoom-pan';
import MediaContent from '@/components/common/media-content/media-content';
import { NativeShareButton } from '@/components/common/native-share-button';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import type { BatchMessage } from '@/hooks/batch-message/use-get-batch-messages';
import useDownloadMessageFile from '@/hooks/use-download-message-file';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import ROUTES from '@/routes';
import copyTextToClipboard from '@/utils/copy-text';
import cn from '@/utils/merge-class-names';

export default function PreviewBatchMessageRoute() {
  const { batchId } = useTypedParams(ROUTES.MY.SETTINGS.MESSAGING.BATCH.PREVIEW);
  const message = useLoaderData<BatchMessage>();
  const imageZoomRef = useRef<ImageZoomActions>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const shouldDarkMode = [ConversationMessageContentType.Image, ConversationMessageContentType.Video].includes(
    message.MsgType,
  );
  const { isDownloading, handleDownload } = useDownloadMessageFile(message);

  return (
    <PageLayout
      className={cn('flex h-full', {
        'text-neutral-0 bg-neutral-950': shouldDarkMode,
      })}
      bannerColor={shouldDarkMode ? 'dark' : 'light'}
      headerConfig={{
        className: cn(
          'transition-colors mode-embedded:bg-primary ',
          {
            'text-neutral-0 bg-neutral-950': shouldDarkMode,
          },
          'layout-mini:text-base-content',
        ),
        actions: (
          <button
            type='button'
            className='btn btn-ghost p-0'
            onClick={() => {
              navigate(-1);
            }}>
            <Add className='rotate-45 size-6' />
          </button>
        ),
        headerLabel: message && (
          <div className='grid grid-cols-[1fr_auto_1fr] w-full items-center gap-2'>
            <div className='flex justify-start'>
              <NativeShareButton
                content={{ type: 'batch-message', data: message }}
                className={cn(' text-neutral-0', {
                  'text-base-content': !shouldDarkMode,
                })}
              />
            </div>

            <div className='flex flex-col text-sm size-full flex-center'>
              <span className='font-bold line-clamp-1 text-center'>
                {message.Recipients.map((r) => r.RecipientName)
                  .filter((n) => !!n)
                  .join(',')}
              </span>
              <DateDisplay
                ruleName='collection-preview'
                timestamp={message.CreateTime}
              />
            </div>

            {message.MsgType === ConversationMessageContentType.Image && (
              <div className='justify-end items-center gap-4 min-w-fit hidden desktop:flex'>
                {
                  <>
                    {[
                      { key: 'zoom-in', icon: <SearchZoomIn />, action: () => imageZoomRef?.current?.zoomIn() },
                      { key: 'zoom-out', icon: <SearchZoomOut1 />, action: () => imageZoomRef?.current?.zoomOut() },
                      {
                        key: 'zoom-fit',
                        icon: <ZoomFit className='size-6' />,
                        action: () => imageZoomRef?.current?.resetTransform(),
                      },
                    ].map(({ key, icon, action }) => (
                      <button
                        key={key}
                        type='button'
                        className='btn btn-ghost p-0'
                        onClick={action}>
                        {icon}
                      </button>
                    ))}
                    <div className='h-6 w-px ms-4 bg-neutral-0' />
                  </>
                }
              </div>
            )}
          </div>
        ),
      }}>
      <div
        className={cn(
          'flex-1',
          'flex items-center',
          'desktop:h-[calc(80dvh-8rem)]',
          message.MsgType === ConversationMessageContentType.Text && 'desktop:h-[calc(80dvh-9rem)]',
        )}>
        <MediaContent
          className={cn(
            'max-h-[80dvh]  desktop:h-[calc(80dvh-8rem)]',
            message.MsgType === ConversationMessageContentType.Text && 'desktop:h-[calc(80dvh-9rem)] px-4',
          )}
          ref={imageZoomRef}
          autoPlay
          isInView
          type={message.MsgType}
          src={message.Message}
        />
      </div>

      {message.MsgType === ConversationMessageContentType.Text ? (
        <div className='h-20 w-full flex justify-around items-center'>
          <button
            type='button'
            className='btn btn-ghost flex-nowrap flex-col gap-2'
            onClick={() => copyTextToClipboard(message.Message)}>
            <Copy
              variant='Outline'
              size={24}
            />
            <span>{t('common.copy')}</span>
          </button>
        </div>
      ) : (
        <div className='h-20 w-full flex justify-around desktop:justify-end items-center'>
          <button
            type='button'
            disabled={isDownloading}
            className='btn btn-ghost flex-nowrap min-h-none desktop:gap-2 desktop:h-10 disabled:invert'
            onClick={handleDownload}>
            {isDownloading ? (
              <span className='loading loading-spinner' />
            ) : (
              <ImportCurve
                variant='Outline'
                size={24}
              />
            )}
            <span className='hidden desktop:inline-block'>{t('common.save')}</span>
          </button>
          <button
            type='button'
            className='btn btn-ghost flex-nowrap min-h-none desktop:gap-2 desktop:h-10 disabled:invert'
            onClick={() =>
              navigate(
                ROUTES.MY.SETTINGS.MESSAGING.BATCH.$.PREVIEW.$.FORWARD.$buildPath({
                  params: { batchId },
                  relative: true,
                }),
              )
            }>
            <ShareArrow className='size-6' />
            <span className='hidden desktop:inline-block'>{t('conversation.forward.forward')}</span>
          </button>
        </div>
      )}
    </PageLayout>
  );
}
